<template>
  <div class="account-settings-container">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <h1 class="page-title">{{ $t("account_settings") }}</h1>
        <p class="page-subtitle">
          Manage your account preferences and settings
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="content-wrapper">
      <!-- Account Information Card -->
      <div class="modern-card account-info-card">
        <div class="card-header">
          <div class="card-icon">
            <v-icon color="#ffff" size="24">mdi-account-circle</v-icon>
          </div>
          <div class="card-title">
            <h3>{{ $t("account_information") }}</h3>
            <p>Update your personal information</p>
          </div>
        </div>

        <div class="card-content">
          <!-- Email Section -->
          <!-- Email Section -->
          <div class="input-group">
            <div class="input-label-container">
              <label class="input-label">Email Address</label>
              <div class="verification-status">
                <v-chip
                  v-if="profile.email_verified"
                  small
                  color="success"
                  text-color="white"
                  class="verification-chip"
                >
                  <v-icon left size="16">mdi-check-circle</v-icon>
                  Verified
                </v-chip>
                <v-chip
                  v-else
                  small
                  color="warning"
                  text-color="white"
                  class="verification-chip"
                >
                  <v-icon left size="16">mdi-alert-circle</v-icon>
                  Not Verified
                </v-chip>
              </div>
            </div>
            <v-text-field
  v-model="email"
  variant="outlined"
  density="comfortable"
  placeholder="Enter your email address"
  @input="onChange"
  :rules="[rules.required, rules.email]"
  class="modern-input"
  :error-messages="emailError || emailErrorMessage"
            />
            <v-btn
              v-if="!profile.email_verified"
              variant="text"
              color="#084468"
              size="small"
              small
              @click="verifyEmail"
              :disabled="disableResendEmail"
              :loading="sendingEmail"
              class="verify-btn"
            >
              {{
                resendEmailCount
                  ? `Resend in ${resendEmailCount}s`
                  : "Verify Email"
              }}
            </v-btn>
          </div>

          <!-- Phone Section -->
          <div class="input-group">
            <div class="input-label-container">
              <label class="input-label">Phone Number</label>
              <div class="verification-status">
                <v-chip
                  v-if="profile.phone_verified"
                  small
                  color="success"
                  text-color="white"
                  class="verification-chip"
                >
                  <v-icon left size="16">mdi-check-circle</v-icon>
                  Verified
                </v-chip>
                <v-chip
                  v-else
                  small
                  color="warning"
                  text-color="white"
                  class="verification-chip"
                >
                  <v-icon left size="16">mdi-alert-circle</v-icon>
                  Not Verified
                </v-chip>
              </div>
            </div>
            <AppPhoneNumberInput
              v-model="phone"
              @input="onChange"
              @update="onUpdatePhone"
              class="modern-phone-input"
            />
<v-alert
  v-if="phoneError || phoneErrorMessage"
  type="error"
  variant="tonal"
  class="error-alert"
>
  {{ phoneError || phoneErrorMessage }}
</v-alert>

            <v-btn
              v-if="!profile.phone_verified"
              variant="text"
              color="#084468"
              size="small"
              @click="verifyPhone"
              class="verify-btn"
              small
              :disabled="disableResendSMS"
              :loading="sendingCode"
              depressed
            >
              {{
                resendEmailCount
                  ? `Resend in ${resendEmailCount}s`
                  : "Verify Phone"
              }}
            </v-btn>
          </div>

          <!-- Error Alert -->
          <v-alert
            v-if="errorMessage"
            type="error"
            variant="tonal"
            class="error-alert"
          >
            {{ errorMessage }}
          </v-alert>
        </div>

        <div class="card-actions">
          <v-btn
            color="#084468"
            variant="elevated"
            size="large"
            @click="onSave"
            :loading="loading"
            :disabled="isSaveDisabled"
            class="save-btn"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Changes
          </v-btn>
        </div>
      </div>

      <!-- Privacy Settings Card (Volunteer) -->
      <div v-if="profileType == 'Volunteer'" class="modern-card privacy-card">
        <div class="card-header">
          <div class="card-icon">
            <v-icon color="#fff" size="24">mdi-shield-account</v-icon>
          </div>
          <div class="card-title">
            <h3>{{ $t("account_privacy") }}</h3>
            <p>Control who can see your profile</p>
          </div>
        </div>

        <div class="card-content">
          <div class="privacy-setting">
            <div class="privacy-question">Who can see your profile?</div>
            <v-btn
              variant="outlined"
              color="primary"
              @click="editSharePrivacy"
              class="privacy-btn"
            >
              <v-icon left>mdi-eye</v-icon>
              {{ profile_share_privacy }}
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Balance Card (Agency) -->
      <div v-if="profileType == 'Agency'" class="modern-card balance-card">
        <div class="card-header">
          <div class="card-icon">
            <v-icon color="#ffff" size="24">mdi-wallet</v-icon>
          </div>
          <div class="card-title">
            <h3>Balance Management</h3>
            <p>Track donations and manage payouts</p>
          </div>
        </div>

        <div class="card-content">
          <div class="balance-info">
            <p class="balance-description">
              View and manage the balance of donations received through our
              platform. Connect your bank account to receive payouts, and track
              the funds you've raised to support your cause.
            </p>

            <div v-if="profile.stripe_account && balance" class="balance-stats">
              <div v-if="balance.available" class="balance-item available">
                <div class="balance-label">Available Balance</div>
                <div class="balance-amount">
                  {{
                    formatCurrency(
                      balance.available.reduce((a, b) => a + b.amount, 0) / 100
                    )
                  }}
                </div>
              </div>

              <div
                v-if="
                  balance.pending &&
                  balance.pending.reduce((a, b) => a + b.amount, 0)
                "
                class="balance-item pending"
              >
                <div class="balance-label">Pending</div>
                <div class="balance-amount">
                  {{
                    formatCurrency(
                      balance.pending.reduce((a, b) => a + b.amount, 0) / 100
                    )
                  }}
                </div>
              </div>

              <div
                v-if="balance.connect_reserved"
                class="balance-item reserved"
              >
                <div class="balance-label">Reserved</div>
                <div class="balance-amount">
                  {{
                    formatCurrency(
                      balance.connect_reserved.reduce(
                        (a, b) => a + b.amount,
                        0
                      ) / 100
                    )
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card-actions">
          <v-btn
            v-if="!profile.stripe_account"
            color="#084468"
            variant="elevated"
            size="large"
            @click="onConnectBalance"
            :loading="connectingBalance"
            class="connect-btn"
          >
            <v-icon left>mdi-bank</v-icon>
            Connect Balance
          </v-btn>
          <div v-else class="balance-actions">
            <v-btn
              color="#084468"
              variant="outlined"
              @click="goToDonations"
              class="action-btn"
            >
              <v-icon left>mdi-chart-line</v-icon>
              View Details
            </v-btn>
            <v-btn
              color="#084468"
              variant="elevated"
              @click="goToOnboarding"
              :loading="connectingBalance"
              class="action-btn"
            >
              <v-icon left>mdi-cog</v-icon>
              Go to Onboarding
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Danger Zone Card -->
      <div class="modern-card danger-card">
        <div class="card-header">
          <div class="card-icon danger-icon">
            <v-icon color="#ffff" size="24">mdi-alert-octagon</v-icon>
          </div>
          <div class="card-title">
            <h3>{{ $t("delete_account") }}</h3>
            <p>Permanently delete your account and all data</p>
          </div>
        </div>

        <div class="card-content">
          <div class="danger-warning">
            <v-icon color="error" class="warning-icon">mdi-alert-circle</v-icon>
            <div class="warning-text">
              <strong>This action cannot be undone.</strong> When you delete
              your {{appTitle}} account, you won't be able to retrieve any
              content or information you've shared.
            </div>
          </div>

          <div v-if="confirmDelete" class="confirmation-section">
            <p class="confirmation-text">
              Please type <strong>DELETE</strong> to confirm your deletion:
            </p>
            <v-text-field
              v-model="confirmDeleteText"
              variant="outlined"
              density="comfortable"
              placeholder="Type DELETE to confirm"
              @input="onChange"
              :error-messages="errorDelete"
              class="confirmation-input"
            />
          </div>
          <v-alert
            v-if="errorDeleteMessage"
            type="error"
            variant="tonal"
            class="error-alert"
          >
            {{ errorDeleteMessage }}
          </v-alert>
        </div>

        <div class="card-actions">
          <v-btn
            color="error"
            variant="elevated"
            size="large"
            @click="onDeleteAccount"
            :loading="deletingAccount"
            class="delete-btn"
          >
            <v-icon left>mdi-delete</v-icon>
            {{ confirmDelete ? "Confirm Delete" : "Delete Account" }}
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Dialogs and Snackbars -->
    <v-snackbar
      v-model="snackbar"
      :timeout="4000"
      location="bottom"
      variant="elevated"
      color="success"
    >
      {{ snackMessage }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <phone-verification-dialog
      :verifyDialog="verifyPhoneDialog"
      :onClose="onCloseVerify"
      v-if="verifyPhoneDialog"
    />

    <volunteer-profile-edit-audience
      :dialog="audienceDialog"
      type="profile"
      :onClose="onClosePrivacyDialog"
      v-if="audienceDialog"
    />

    <ConfirmDeleteDialogVue
      v-if="disconnectDialog"
      text="Are you sure do you want to disconnect?"
      :on-close="onCloseDisconnectDialog"
      :on-confirm="onConfirmDisconnect"
      :deleteDialog="disconnectDialog"
      delete-button-text="Yes, Disconnect it"
    />
  </div>
</template>


<script>
import AppPhoneNumberInput from "@/components/common/AppPhoneNumberInput.vue";
import PhoneVerificationDialog from "@/components/common/PhoneVerificationDialog.vue";
import VolunteerProfileEditAudience from "../volunteer/profile/edit/VolunteerProfileEditAudience";
import { mapActions, mapGetters } from "vuex";
import ConfirmDeleteDialogVue from "../../components/common/ConfirmDeleteDialog.vue";

export default {
  components: {
    AppPhoneNumberInput,
    PhoneVerificationDialog,
    VolunteerProfileEditAudience,
    ConfirmDeleteDialogVue,
  },
  data() {
    return {
      email: "",
      phone: "",
      profile_share_privacy: "",
      phonePayload: {},
      rules: {
        required: (value) => !!value || "Required.",
        email: (value) => {
          if (!value || typeof value !== "string")
            return "Invalid email address";
          // Local part must start and end with alphanumeric, no consecutive dots, no leading/trailing dot, no whitespace
          const emailPattern =
            /^[a-zA-Z0-9](?:[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]{0,62}[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/;
          const [local, domain] = value.split("@");
          if (
            !emailPattern.test(value) ||
            /\s/.test(value) ||
            !local ||
            !domain ||
            local.startsWith(".") ||
            local.endsWith(".") ||
            local.includes("..") ||
            !/^[a-zA-Z0-9]/.test(local) || // must start with alphanumeric
            !/[a-zA-Z0-9]$/.test(local) // must end with alphanumeric
          ) {
            return "Invalid email address";
          }
          return true;
        },
      },
      shareIcons: {
        "Only me": "mdi-lock",
        "Only agencies I fanned": "mdi-domain",
        "Only followed volunteers": "mdi-account-multiple",
        "All followed agencies and volunteers": "mdi-account-group",
        Public: "mdi-earth",
      },
      loading: false,
      connectingBalance: false,
      sendingEmail: false,
      snackbar: false,
      snackMessage: null,
      errorMessage: null,
      resendEmailCount: 0,
      emailError: null,
      phoneError: null,
      disableResendEmail: false,
      sendingCode: false,
      disableResendSMS: false,
      verifyPhoneDialog: false,
      confirmDelete: false,
      audienceDialog: false,
      deletingAccount: false,
      confirmDeleteText: "",
      errorDelete: null,
      errorDeleteMessage: null,
      selectedSocialType: "",
      disconnectDialog: false,
      socialConnectedChannel: new BroadcastChannel("social_connected_callback"),
      balance: null,
          isEmailDuplicate: false,
    isPhoneDuplicate: false,
    emailErrorMessage: "",
    phoneErrorMessage: "",
    emailCheckTimeout: null,
    phoneCheckTimeout: null,
    lastCheckedEmail: "",
    lastCheckedPhone: "",
    };
  },
  methods: {
    ...mapActions({
      updateProfile: "auth/updateProfile",
      sendEmailVerification: "auth/sendEmailVerification",
      sendVerificationCode: "auth/sendVerificationCode",
      deleteAccount: "auth/deleteAccount",
      requestTwitterToken: "auth/requestTwitterToken",
      disconnectSocial: "auth/disconnectSocial",
      connectAgencyBalance: "agency/connectBalance",
      getBalance: "agency/getBalance",
      getOnboardingUrl: "agency/createOnboardingUrl",
      checkEmail: "auth/onCheckEmail",
      checkPhone: "auth/onCheckPhone"
    }),
  onChange() {
    this.errorMessage = null;
    this.errorDelete = null;
    this.errorDeleteMessage = null;
    this.validateEmail();
    this.validatePhone();

    // Debounced duplicate check for email
    if (this.emailCheckTimeout) clearTimeout(this.emailCheckTimeout);
    if (this.email && this.email !== this.profile.email) {
      this.emailCheckTimeout = setTimeout(() => {
        this.checkEmailAddress();
      }, 400);
    } else {
      // If reverted to original, clear duplicate error
      this.isEmailDuplicate = false;
      this.emailErrorMessage = "";
    }

    // Debounced duplicate check for phone
    if (this.phoneCheckTimeout) clearTimeout(this.phoneCheckTimeout);
    if (this.phone && this.phone !== this.profile.phone) {
      this.phoneCheckTimeout = setTimeout(() => {
        this.checkPhoneNumber();
      }, 400);
    } else {
      this.isPhoneDuplicate = false;
      this.phoneErrorMessage = "";
    }
  },
  async checkEmailAddress() {
    if (this.email === this.profile.email) {
      this.isEmailDuplicate = false;
      this.emailErrorMessage = "";
      return;
    }
    try {
      await this.checkEmail({ email: this.email });
      this.isEmailDuplicate = false;
      this.emailErrorMessage = "";
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.email === this.profile.email
      ) {
        this.isEmailDuplicate = false;
        this.emailErrorMessage = "";
      } else if (error.response && error.response.data) {
        this.isEmailDuplicate = true;
        this.emailErrorMessage = error.response.data.message || "Email already exists.";
      } else {
        this.isEmailDuplicate = true;
        this.emailErrorMessage = "Unable to check email. Please try again.";
      }
    }
  },
  async checkPhoneNumber() {
    if (this.phone === this.profile.phone) {
      this.isPhoneDuplicate = false;
      this.phoneErrorMessage = "";
      return;
    }
    try {
      await this.checkPhone({ phone: this.phone });
      this.isPhoneDuplicate = false;
      this.phoneErrorMessage = "";
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.phone === this.profile.phone
      ) {
        this.isPhoneDuplicate = false;
        this.phoneErrorMessage = "";
      } else if (error.response && error.response.data) {
        this.isPhoneDuplicate = true;
        this.phoneErrorMessage = error.response.data.message || "Phone number already exists.";
      } else {
        this.isPhoneDuplicate = true;
        this.phoneErrorMessage = "Unable to check phone. Please try again.";
      }
    }
  },
robustEmailValidator(value) {
  if (!value || typeof value !== "string") return false;
  // Strict email pattern: local part must start with a letter, end with alphanumeric, no consecutive dots, no leading/trailing dot, no whitespace, etc.
  const emailPattern =
    /^[a-zA-Z][a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]{0,62}[a-zA-Z0-9]@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/;
  const [local, domain] = value.split("@");
  if (
    !emailPattern.test(value) ||
    /\s/.test(value) ||
    !local ||
    !domain ||
    local.startsWith(".") ||
    local.endsWith(".") ||
    local.includes("..") ||
    !/^[a-zA-Z]/.test(local) || // must start with a letter
    !/[a-zA-Z0-9]$/.test(local) // must end with alphanumeric
  ) {
    return false;
  }
  // Disallow local part that is all uppercase letters
  if (/^[A-Z]+$/.test(local)) {
    return false;
  }
  // Disallow local part that starts with a letter followed by special characters
  if (/^[a-zA-Z][^a-zA-Z0-9]/.test(local)) {
    return false;
  }
  return true;
},
    onUpdatePhone(payload) {
      this.phonePayload = payload;
    },
  validateEmail() {
    if (!this.email) {
      this.emailError = "Required.";
      return false;
    }
    if (!this.robustEmailValidator(this.email)) {
      this.emailError = "Invalid email address.";
      return false;
    }
    if (this.isEmailDuplicate) {
      this.emailError = this.emailErrorMessage || "Email already exists.";
      return false;
    }
    this.emailError = null;
    return true;
  },
  validatePhone() {
    if (!this.phone) {
      this.phoneError = "Required.";
      return false;
    }
    if (this.phonePayload && this.phonePayload.isValid === false) {
      this.phoneError = "Invalid phone number.";
      return false;
    }
    if (this.isPhoneDuplicate) {
      this.phoneError = this.phoneErrorMessage || "Phone number already exists.";
      return false;
    }
    this.phoneError = null;
    return true;
  },
    onSave() {
      this.errorMessage = null;
      this.emailError = null;
      this.phoneError = null;

      // Validate email and phone before submit
      const emailValid = this.validateEmail();
      const phoneValid = this.validatePhone();

      if (!emailValid || !phoneValid) {
        this.errorMessage = "Please correct the errors above before saving.";
        return;
      }

      if (this.email || this.phone) {
        this.loading = true;
        let phone = this.phone;
        if (
          this.phonePayload.isValid &&
          this.phonePayload.countryCallingCode != "1"
        ) {
          phone = `+${this.phonePayload.countryCallingCode} ${phone}`;
        }
        this.updateProfile({ email: this.email, phone })
          .then(() => {
            this.loading = false;
            this.email = this.profile.email;
            this.phone = this.profile.phone;
            let str = "";
            if (!this.profile.email_verified) str = " Please verify your email";
            if (!this.profile.phone_verified)
              str = str
                ? str + " and phone number"
                : "Please verify your phone number";
            this.showSnackMessage(
              "Updated info successfully! " + (str ? str + "." : "")
            );
          })
          .catch((error) => {
            this.errorMessage = error.response.data.message;
            this.loading = false;
          });
      }
    },
    verifyEmail() {
      if (this.email != this.profile.email) {
        this.errorMessage = "Please save the updated email first.";
        return;
      }
      this.sendingEmail = true;
      this.sendEmailVerification()
        .then(() => {
          this.sendingEmail = false;
          this.disableResendEmail = true;
          this.resendEmailCount = 60;
          this.countDownEmail();
        })
        .catch((error) => {
          console.log(error);
          this.sendingEmail = false;
        });
    },
    verifyPhone() {
      if (this.phone != this.profile.phone) {
        this.errorMessage = "Please save the updated phone number first.";
        return;
      }
      this.sendingCode = true;
      this.sendVerificationCode()
        .then(() => {
          this.sendingCode = false;
          this.verifyPhoneDialog = true;
        })
        .catch((error) => {
          console.log(error.response.data.message);
        });
    },
    editSharePrivacy() {
      this.audienceDialog = true;
    },
    onClosePrivacyDialog() {
      this.audienceDialog = false;
    },
    showSnackMessage(message) {
      this.snackbar = true;
      this.snackMessage = message;
    },
    countDownEmail() {
      if (this.resendEmailCount > 0) {
        setTimeout(() => {
          this.resendEmailCount -= 1;
          this.countDownEmail();
        }, 1000);
      } else {
        this.disableResendEmail = false;
      }
    },
    onCloseVerify() {
      this.verifyPhoneDialog = false;
    },
    onCloseDisconnectDialog() {
      this.disconnectDialog = false;
    },
    onConfirmDisconnect() {
      this.disconnectDialog = false;
      this.disconnectSocial({ provider: this.selectedSocialType });
    },
    onDeleteAccount() {
      if (!this.confirmDelete) {
        this.confirmDelete = true;
        this.confirmDeleteText = "";
      } else {
        if (this.confirmDeleteText == "DELETE") {
          this.deletingAccount = true;
          this.deleteAccount()
            .then(() => {
              this.deletingAccount = false;
              this.$router.push({ name: "login" });
            })
            .catch((error) => {
              this.deletingAccount = false;
              this.errorDeleteMessage = error.response.data.message;
              this.confirmDeleteText = "";
              console.log(error);
            });
        } else if (this.confirmDeleteText == "") {
          this.errorDelete = "Required";
        } else {
          this.errorDelete = "Type correctly";
        }
      }
    },
    formatCurrency(numberString) {
      const number = parseFloat(`${numberString}`);
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(number);
    },
    onConnectBalance() {
      this.connectingBalance = true;
      this.connectAgencyBalance()
        .then(() => {
          this.connectingBalance = false;
          location.reload();
        })
        .catch((error) => {
          this.connectingBalance = false;
          this.showSnackMessage(error.response.data.message);
        });
    },
    goToDonations() {
      this.$router.push({
        name: "donation-history",
        query: { filter: "received" },
      });
    },
    goToOnboarding() {
      this.connectingBalance = true;
      this.getOnboardingUrl()
        .then((res) => {
          this.connectingBalance = false;
          console.log(res);
          window.open(res.url, "_blank");
        })
        .catch((error) => {
          this.connectingBalance = false;
          this.showSnackMessage(error.response.data.message);
        });
    },
  },
  computed: {
    ...mapGetters("auth", {
      profile: "getProfile",
      profileType: "getType",
    }),
  isSaveDisabled() {
    // Disable if loading, invalid, or duplicate
    return (
      this.loading ||
      !this.validateEmail() ||
      !this.validatePhone() ||
      this.isEmailDuplicate ||
      this.isPhoneDuplicate
    );
  },
      appTitle() {
      return process.env.VUE_APP_TITLE;
    }
  },
  watch: {
    profile(newValue) {
      this.email = newValue.email;
      this.phone = newValue.phone;
      this.profile_share_privacy = newValue.profile_share_privacy;
      if (!this.profile_share_privacy) this.profile_share_privacy = "Only me";

      if (newValue.stripe_account) {
        this.getBalance()
          .then((res) => {
            this.balance = res;
            console.log(res);
          })
          .catch((error) => console.log(error));
      }
    },
  },
  mounted() {
    this.email = this.profile.email;
    this.phone = this.profile.phone;
    this.profile_share_privacy = this.profile.profile_share_privacy;
    if (!this.profile_share_privacy) this.profile_share_privacy = "Only me";

    this.socialConnectedChannel.onmessage = (event) => {
      this.showSnackMessage(event.data);
      location.reload();
    };

    if (this.profile.stripe_account) {
      this.getBalance()
        .then((res) => {
          this.balance = res;
          console.log(res);
        })
        .catch((error) => console.log(error));
    }
  },
  beforeDestroy() {
    this.socialConnectedChannel.close();
  },
};
</script>


<style scoped>
.account-settings-container {
  min-height: 100vh;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  padding: 0;
}

.header-section {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 2rem 0;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.page-subtitle {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0.5rem 0 0 0;
}

.content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.modern-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
}

.modern-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
}

.card-header {
  display: flex;
  align-items: center;
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.card-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.card-icon.danger-icon {
  background: linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%);
  box-shadow: 0 4px 12px rgba(255, 65, 108, 0.3);
}

.card-title h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.card-title p {
  color: #6c757d;
  margin: 0.25rem 0 0 0;
  font-size: 0.9rem;
}

.card-content {
  padding: 2rem;
}

.card-actions {
  padding: 1.5rem 2rem 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  background: rgba(248, 249, 250, 0.5);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.input-group {
  margin-bottom: 2rem;
}

.input-label-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.input-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.verification-status {
  display: flex;
  align-items: center;
}

.verification-chip {
  font-size: 0.75rem;
  height: 24px;
}

.modern-input :deep(.v-field) {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.modern-input :deep(.v-field--focused) {
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.2);
}

.verify-btn {
  margin-top: 0.5rem;
  border-radius: 8px;
  color: #fff;
}

.privacy-setting {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: rgba(102, 126, 234, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.privacy-question {
  font-weight: 600;
  color: #2c3e50;
}

.privacy-btn {
  border-radius: 8px;
}

.balance-info {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.balance-description {
  color: #6c757d;
  line-height: 1.6;
  margin: 0;
}

.balance-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.balance-item {
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.balance-item.available {
  background: linear-gradient(135deg, #00b894 0%, #00a085 100%);
  color: white;
}

.balance-item.pending {
  background: linear-gradient(135deg, #fdcb6e 0%, #e17055 100%);
  color: white;
}

.balance-item.reserved {
  background: linear-gradient(135deg, #74b9ff 0%, #0984e3 100%);
  color: white;
}

.balance-label {
  font-size: 0.9rem;
  opacity: 0.9;
  margin-bottom: 0.5rem;
}

.balance-amount {
  font-size: 1.5rem;
  font-weight: 700;
}

.balance-actions {
  display: flex;
  gap: 1rem;
}

.danger-warning {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 65, 108, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 65, 108, 0.1);
  margin-bottom: 1.5rem;
}

.warning-icon {
  margin-top: 0.125rem;
}

.warning-text {
  color: #721c24;
  line-height: 1.6;
}

.confirmation-section {
  margin-top: 1.5rem;
}

.confirmation-text {
  color: #6c757d;
  margin-bottom: 1rem;
}

.confirmation-input :deep(.v-field) {
  border-radius: 12px;
}

.error-alert {
  margin-top: 1rem;
  border-radius: 12px;
}

.save-btn,
.connect-btn,
.delete-btn {
  border-radius: 12px;
  padding: 0 2rem;
  font-weight: 600;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  color: #ffff;
}

.action-btn {
  border-radius: 8px;
  text-transform: none;
  color: #ffff;
}
</style>

